import React from 'react'
import { useState } from 'react'
import { ExpandableSection, Tooltip } from '@enterprise-ui/canvas-ui-react'
import {
  COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS,
  APPROVAL_STATUS_ENUMS,
  APPROVED_APPROVAL_STATUSES,
  PENDING_APPROVAL_STATUSES,
  COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
  dateTimeToShortString,
} from '../../globalConfig/constants'

export const SubmissionRecord = React.memo(({ recordData }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const primaryFields = [
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.SUBMITTED_ON,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_TYPE,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.VENDOR_ID,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.APPROVAL_STATUS,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.NEW_UNIT_COST,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.START_DATE,
  ]

  const leftSideSecondaryFields = [
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_TYPE,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_VALUES,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.SUBMITTED_ON,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.SUBMITTED_BY,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.SOURCE_LABEL,
  ]

  const rightSideSecondaryFields = [
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.APPROVAL_STATUS_LABEL,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.DECISION_DATE,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.DECISION_REASON,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.APPROVED_BY,
    COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.END_DATE,
  ]

  const onExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const getPrimaryText = (field) => {
    if (
      field.keyName ===
        COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_TYPE
          .keyName &&
      recordData[field.variableName] !==
        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.CHAIN.variableName
    ) {
      return getLocationLevelJSX()
    } else if (
      field.keyName ===
      COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.SUBMITTED_ON.keyName
    ) {
      return dateTimeToShortString(recordData[field.variableName])
    } else if (
      field.keyName ===
      COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.APPROVAL_STATUS.keyName
    ) {
      return Object.values(APPROVAL_STATUS_ENUMS).find(
        (status) => status.enum === recordData[field.variableName],
      )?.shortLabel
    } else if (field?.tableDetails?.toStringFunc) {
      return field.tableDetails.toStringFunc(recordData[field.variableName])
    } else {
      return recordData[field.variableName]
    }
  }

  const getSecondaryText = (field) => {
    if (field?.tableDetails?.toStringFunc) {
      return (
        <>
          <strong>{field.label + ': '}</strong>
          {`${field.tableDetails.toStringFunc(recordData[field.variableName]) || 'N/A'}`}
        </>
      )
    } else {
      return (
        <>
          <strong>{field.label + ': '}</strong>
          {`${recordData[field.variableName] || 'N/A'}`}
        </>
      )
    }
  }

  const getLocationLevelJSX = () => {
    const locationLevelField =
      COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_TYPE
    const locationLevelValues =
      recordData[
        COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.COST_LOCATION_GROUP_VALUES
          .variableName
      ]
    const labelText =
      recordData[locationLevelField.variableName] ===
      COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.STATE.variableName
        ? 'States'
        : recordData[locationLevelField.variableName] ===
            COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.DC.variableName
          ? 'DCs'
          : recordData[locationLevelField.variableName] ===
              COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.CUSTOM.variableName
            ? 'Custom Groups'
            : 'Locations'

    return (
      <Tooltip content={locationLevelValues.join(', ')} location="bottom-left">
        <span>{`${labelText}: ${locationLevelValues.length}`}</span>
      </Tooltip>
    )
  }

  const getMainBackgroundColorClassName = () => {
    const status =
      recordData[
        COST_CHANGE_INSIGHTS_SUBMISSION_RESULTS.APPROVAL_STATUS.variableName
      ]
    if (APPROVED_APPROVAL_STATUSES.includes(status)) {
      return 'hesp-cv-results-background-approved'
    } else if (PENDING_APPROVAL_STATUSES.includes(status)) {
      return 'hesp-cv-results-background-pending'
    } else {
      return 'hesp-cv-results-background-rejected'
    }
  }

  return (
    <div className="hesp-general-flex hc-mb-xs">
      <ExpandableSection
        className={`hesp-general-expandable hesp-general-fullwidth hesp-black-text ${getMainBackgroundColorClassName()}`}
        padding="none"
        preserveDom
        onExpand={onExpand}
        expanded={isExpanded}
      >
        <div className="hc-fs-lg hesp-general-flex hesp-general-alignItemsCenter">
          {primaryFields.map((primaryField) => {
            return (
              <span
                className="hesp-general-flex-grow hesp-general-centeredText"
                key={`${primaryField.variableName}-submission-record-header`}
                style={{ flexBasis: 0 }}
              >
                {getPrimaryText(primaryField)}
              </span>
            )
          })}
        </div>
        <ExpandableSection.Content>
          <div className="hesp-general-flex">
            <div className="hesp-cv-results-secondaryText-container">
              {leftSideSecondaryFields.map((secondaryField, index) => {
                return (
                  <span
                    key={`${secondaryField.variableName}-submission-record-secondary-text`}
                  >
                    {getSecondaryText(secondaryField)}
                  </span>
                )
              })}
            </div>
            <div className="hesp-cv-results-secondaryText-container">
              {rightSideSecondaryFields.map((secondaryField, index) => {
                return (
                  <span
                    key={`${secondaryField.variableName}-submission-record-secondary-text`}
                  >
                    {getSecondaryText(secondaryField)}
                  </span>
                )
              })}
            </div>
          </div>
        </ExpandableSection.Content>
      </ExpandableSection>
    </div>
  )
})
